import React, {useRef, useState} from 'react';
import {Button, Form} from 'react-bootstrap';
import './saveSearchComponent.scss';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useSearchPageType} from '../search-hooks';
import {saveSearchItem} from './duck/saveSearchComponent-actions';
import {Option} from '../../common/select/select';
import {Checkbox} from '../../common/checkbox/checkbox';
import {HibuSelect} from '../../common/select/hibuSelect';
import classNames from 'clsx';
import createOption from '../../common/select/hibuSelectUtil';
import {SaveSearchModal} from '../../common/hibuModal/hibuModal';

type SaveSearchComponentProps = {
    folderList: Option[],
    savedSearchesSharedByOthersEnabled: boolean,
}

export const SaveSearchComponent = React.memo(({
                                                   folderList,
                                                   savedSearchesSharedByOthersEnabled
                                               }: SaveSearchComponentProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const inputRef = useRef<HTMLInputElement>(null);

    const [showModal, setShowModal] = useState<boolean>(false);
    const [searchNameInput, setSearchNameInput] = useState<string>('');
    const [folderInput, setFolderInput] = useState<string>('');
    const [selectedFolder, setSelectedFolder] = useState<Option | undefined>(undefined);
    const [isShared, setIsShared] = useState<boolean>(false);

    const addNewSearch = () => {
        setShowModal(true);
        setSelectedFolder(undefined);
    };

    const onHide = () => {
        setShowModal(false);
    };

    const onSubmit = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        if (selectedFolder) {
            dispatch(saveSearchItem(searchPageType, searchNameInput, selectedFolder.value, isShared));
            setShowModal(false);
            setSelectedFolder(undefined);
        }
    };

    const onNameEntered = (e: { target: { value: React.SetStateAction<string>; }; }) => {
        setSearchNameInput(e.target.value);
    };

    const onFolderSelected = (e: React.SetStateAction<Option | undefined>) => {
        setSelectedFolder(e);
    };

    const onFolderValueEntered = (newValue: string, action: { action: string; }) => {
        if (action.action === 'input-change' || action.action === 'set-value') {
            setFolderInput(newValue);
        }
        if (folderInput && (action.action === 'menu-close' || action.action === 'input-blur')) {
            const newOption: Option = createOption(folderInput);
            const isNewFolder = folderList?.every(option => option.label !== newOption.label);
            if (isNewFolder) {
                setSelectedFolder(newOption);
            }
        }
    };

    const canBeSaved = (): boolean => {
        return selectedFolder?.value && searchNameInput;
    };

    return (
        <div id={'save-search'}>
            <Button onClick={addNewSearch} id={'save-search-button'}>
                {t('search.saveSearch.action.saveSearch')}
            </Button>
            <SaveSearchModal
                show={showModal}
                onHide={onHide}
                title={t('search.saveSearch.action.saveSearch')}
                cancel={t('general.cancel')}
                save={t('general.save')}
                formId={'save-search-form'}
                canBeSaved={canBeSaved()}
            >
                <Form id={'save-search-form'} onSubmit={onSubmit}>
                    <Form.Group>
                        <Form.Label>{'Name'}</Form.Label>
                        <Form.Control required type="text" onChange={onNameEntered} ref={inputRef}/>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>{t('search.saveSearch.action.folderTitle')}</Form.Label>
                        <HibuSelect
                            name="save-search-select"
                            className={classNames('form-control', 'searchable')}
                            value={selectedFolder}
                            options={folderList}
                            onChange={onFolderSelected}
                            onInputChange={onFolderValueEntered}
                            isSearchable={true}
                            placeholder={t('search.saveSearch.action.folderPlaceholder')}
                        />
                    </Form.Group>
                    {savedSearchesSharedByOthersEnabled && (
                        <Form.Group>
                            <Form.Label>{t('search.saveSearch.action.sharedTitle')}</Form.Label>
                            <Checkbox
                                className={'form-control-checkbox'}
                                labelKey={'search.saveSearch.action.share'}
                                onChange={setIsShared}
                                value={isShared}
                                mayEdit={true}
                            />
                        </Form.Group>
                    )}
                </Form>
            </SaveSearchModal>
        </div>
    );
});
